import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  Grid,
  Paper,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Fade,
  IconButton,
  Tooltip,
  Divider,
  Alert
} from '@mui/material';
import {
  Quiz,
  Timer,
  Assignment,
  PlayArrow,
  ArrowBack,
  CheckCircle,
  Cancel,
  NavigateNext,
  NavigateBefore,
  Flag,
  FlagOutlined,
  Info,
  School,
  Security,
  Lock,
  Pause,
  PlayCircleOutline,
  AccessTime,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api';

const STATUS = { NOT_VISITED: 'not_visited', UNANSWERED: 'unanswered', ANSWERED: 'answered', FLAGGED: 'flagged' };
const STATUS_COLORS = {
  not_visited: { bg: '#f8f9fa', border: '#dee2e6', text: '#374151' },
  unanswered: { bg: '#fff3cd', border: '#ffc107', text: '#856404' },
  answered: { bg: '#d1e7dd', border: '#198754', text: '#0f5132' },
  flagged: { bg: '#f8d7da', border: '#dc3545', text: '#842029' },
};

const TopicTest = () => {
  const { test_id } = useParams();
  const navigate = useNavigate();

  // Add CSS animations
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulse {
        0% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.05); opacity: 0.8; }
        100% { transform: scale(1); opacity: 1; }
      }
      @keyframes bounce {
        0%, 20%, 53%, 80%, 100% { transform: translateY(0); }
        40%, 43% { transform: translateY(-10px); }
        70% { transform: translateY(-5px); }
        90% { transform: translateY(-2px); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // States
  const [testData, setTestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testStarted, setTestStarted] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [questionStartTimes, setQuestionStartTimes] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [showResultsDialog, setShowResultsDialog] = useState(false);
  const [showProcessing, setShowProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState('processing'); // 'processing', 'success', 'results'
  const [testCompleted, setTestCompleted] = useState(false);
  const [alreadyCompletedData, setAlreadyCompletedData] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [questionStatus, setQuestionStatus] = useState({});
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());

  // Security check - verify user authentication
  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');

    if (!token || role?.toLowerCase() !== 'student') {
      toast.error('Unauthorized access. Please login as a student.');
      navigate('/login');
      return;
    }

    setIsAuthorized(true);
  }, [navigate]);

  // Fetch test data with security validation
  useEffect(() => {
    if (!isAuthorized || !test_id) return;

    const fetchTestData = async () => {
      try {
        const response = await api.get(`/student/topic/test/${test_id}`);

        if (!response.data.success) {
          throw new Error(response.data.message || 'Failed to fetch test data');
        }

        const data = response.data.data;

        if (data.test?.is_completed) {
          setAlreadyCompletedData({
            name: data.test.name || '',
            best_score: data.test.best_score ?? null,
            total_questions: data.test.total_questions ?? data.test.questions?.length ?? null,
            total_attempts: data.test.total_attempts ?? 1,
          });
          return;
        }

        setTestData(data);

        // Calculate total time for all questions
        const totalTime = data.test.questions.reduce(
          (total, question) => total + parseInt(question.duration_in_sec), 0
        );
        setTimeRemaining(totalTime);
      } catch (error) {
        console.error('Test fetch error:', error);

        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('role');
          toast.error('Session expired. Please login again.');
          navigate('/login');
        } else if (error.response?.status === 403) {
          toast.error('You do not have permission to access this test.');
          navigate(-1);
        } else if (error.response?.status === 404) {
          toast.error('Test not found or no longer available.');
          navigate(-1);
        } else {
          toast.error('Failed to load test. Please try again.');
          navigate(-1);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTestData();
  }, [test_id, navigate, isAuthorized]);

  // Timer effect with security checks
  useEffect(() => {
    let timer;
    if (testStarted && timeRemaining > 0 && isAuthorized && !isPaused) {
      timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleSubmitTest();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [testStarted, timeRemaining, isAuthorized, isPaused]);

  // Prevent unauthorized access
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && testStarted) {
        toast.warning('Test paused due to tab switch. Please stay focused on the test.');
      }
    };

    const handleBeforeUnload = (e) => {
      if (testStarted) {
        e.preventDefault();
        e.returnValue = 'Are you sure you want to leave? Your test progress will be lost.';
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [testStarted]);

  const handleStartTest = () => {
    if (!isAuthorized) {
      toast.error('Unauthorized access detected.');
      return;
    }
    setTestStarted(true);
    const currentQuestion = testData.test.questions[0];
    setQuestionStartTimes(prev => ({ ...prev, [currentQuestion.id]: Date.now() }));
    setQuestionStatus(prev => ({
      ...prev,
      [currentQuestion.id]: prev[currentQuestion.id] ?? STATUS.UNANSWERED,
    }));
  };

  const handleAnswerChange = (questionId, optionId) => {
    if (!isAuthorized || !testStarted || isPaused) return;
    setAnswers(prev => ({ ...prev, [questionId]: optionId }));
    setQuestionStatus(prev => ({ ...prev, [questionId]: STATUS.ANSWERED }));
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < testData.test.questions.length - 1) {
      const nextIndex = currentQuestionIndex + 1;
      const nextQuestion = testData.test.questions[nextIndex];
      setQuestionStartTimes(prev => ({ ...prev, [nextQuestion.id]: Date.now() }));
      setQuestionStatus(prev => ({
        ...prev,
        [nextQuestion.id]: prev[nextQuestion.id] ?? STATUS.UNANSWERED,
      }));
      setCurrentQuestionIndex(nextIndex);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      const prevIndex = currentQuestionIndex - 1;
      const prevQuestion = testData.test.questions[prevIndex];
      setQuestionStartTimes(prev => ({
        ...prev,
        [prevQuestion.id]: prev[prevQuestion.id] || Date.now(),
      }));
      setCurrentQuestionIndex(prevIndex);
    }
  };

  const handlePause = () => setIsPaused(true);
  const handleResume = () => setIsPaused(false);

  const goToQuestion = (index) => {
    const q = testData.test.questions[index];
    setQuestionStartTimes(prev => ({ ...prev, [q.id]: prev[q.id] || Date.now() }));
    setQuestionStatus(prev => ({ ...prev, [q.id]: prev[q.id] ?? STATUS.UNANSWERED }));
    setCurrentQuestionIndex(index);
  };

  const handleFlagQuestion = (questionId) => {
    setFlaggedQuestions(prev => {
      const next = new Set(prev);
      if (next.has(questionId)) {
        next.delete(questionId);
        setQuestionStatus(p => ({
          ...p,
          [questionId]: answers[questionId] ? STATUS.ANSWERED : STATUS.UNANSWERED,
        }));
      } else {
        next.add(questionId);
        setQuestionStatus(p => ({ ...p, [questionId]: STATUS.FLAGGED }));
      }
      return next;
    });
  };

  const handleSubmitTest = async () => {
    // Prevent re-submission if test is already completed
    if (testCompleted) {
      toast.warning('Test already completed. Please go back to content.');
      return;
    }

    setShowConfirmDialog(false);
    setSubmitting(true);
    setShowProcessing(true);
    setProcessingStep('processing');

    try {
      // Calculate time taken for each question
      const currentTime = Date.now();
      const answersArray = testData.test.questions.map(question => {
        const startTime = questionStartTimes[question.id] || currentTime;
        const timeTaken = Math.floor((currentTime - startTime) / 1000); // Convert to seconds

        return {
          question_id: question.id,
          option_id: answers[question.id] ? parseInt(answers[question.id]) : null,
          time_taken: timeTaken > 0 ? timeTaken : 1 // Minimum 1 second
        };
      }).filter(answer => answer.option_id !== null); // Only include answered questions

      const submitData = {
        answers: answersArray
      };

      const response = await api.post(`/student/topic/test/${test_id}/submit`, submitData);

      if (response.data.success) {
        // Show success step
        setProcessingStep('success');

        // Wait 1200ms then show results
        setTimeout(() => {
          setTestResults(response.data.data);
          setTestCompleted(true);
          setShowProcessing(false);
          setShowResultsDialog(true);
          toast.success(response.data.message || 'Test submitted successfully!');

          // Auto-navigate back after 5 seconds
          setTimeout(() => {
            setShowResultsDialog(false);
            navigate(-1);
          }, 5000);
        }, 1200);
      } else {
        throw new Error(response.data.message || 'Failed to submit test');
      }

    } catch (error) {
      console.error('Test submission error:', error);

      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        toast.error(error.response.data.message || 'Session expired. Please login again.');
        navigate('/login');
      } else if (error.response?.status === 404) {
        toast.error(error.response.data.message || 'Test not found.');
        navigate(-1);
      } else if (error.response?.status === 400) {
        toast.error(error.response.data.message || 'Week information not found for this test.');
      } else if (error.response?.status === 403) {
        const message = error.response.data.message || 'You cannot access this test right now.';
        toast.error(message);
        // Show detailed time restriction message
        if (message.includes('available from')) {
          toast.warning('Please check the test schedule and try again during the allowed time period.');
        }
      } else if (error.response?.status === 422) {
        const errors = error.response.data.errors;
        if (errors) {
          // Handle specific validation errors
          const errorMessages = [];

          // Check for test completion error
          if (errors.test_id && errors.test_id.includes('You have already completed this test.')) {
            toast.error('You have already completed this test.');
            navigate(-1);
            return;
          }

          // Handle answers validation errors
          if (errors.answers) {
            errorMessages.push(...errors.answers);
          }

          // Handle individual answer field errors
          Object.keys(errors).forEach(key => {
            if (key.startsWith('answers.')) {
              errorMessages.push(...errors[key]);
            }
          });

          if (errorMessages.length > 0) {
            toast.error(errorMessages.join(', '));
          } else {
            toast.error(error.response.data.message || 'Validation error occurred.');
          }
        } else {
          toast.error(error.response.data.message || 'Validation error occurred.');
        }
      } else if (error.response?.status === 500) {
        toast.error(error.response.data.message || 'An error occurred while submitting the test.');
      } else {
        toast.error(error.message || 'Failed to submit test. Please check your connection and try again.');
      }
    } finally {
      setSubmitting(false);
      setShowProcessing(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getAnsweredCount = () => {
    return Object.keys(answers).length;
  };

  // Security check loading
  if (!isAuthorized) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <Card sx={{ p: 4, textAlign: 'center', maxWidth: 400 }}>
            <Avatar sx={{ width: 60, height: 60, mx: 'auto', mb: 2, bgcolor: 'error.main' }}>
              <Lock sx={{ fontSize: 30 }} />
            </Avatar>
            <Typography variant="h6" gutterBottom color="error">
              Access Denied
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You need to be logged in as a student to access this test.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <Avatar sx={{ width: 60, height: 60, mx: 'auto', mb: 2, bgcolor: 'primary.main' }}>
              <Quiz sx={{ fontSize: 30 }} />
            </Avatar>
            <Typography variant="h6" gutterBottom>Loading Test...</Typography>
            <LinearProgress sx={{ mt: 2, width: 200 }} />
            <Typography variant="caption" sx={{ mt: 2, display: 'block' }}>
              Verifying access permissions...
            </Typography>
          </Card>
        </Box>
      </Container>
    );
  }

  if (alreadyCompletedData) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Fade in timeout={600}>
          <Card sx={{ borderRadius: '20px', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.12)' }}>
            <Box sx={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              p: 4,
              textAlign: 'center'
            }}>
              <Avatar sx={{ width: 70, height: 70, mx: 'auto', mb: 2, bgcolor: 'rgba(255,255,255,0.25)' }}>
                <CheckCircle sx={{ fontSize: 40 }} />
              </Avatar>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Test Already Completed
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {alreadyCompletedData.name}
              </Typography>
            </Box>
            <CardContent sx={{ p: 4 }}>
              <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
                <Grid container spacing={2} justifyContent="center">
                  {alreadyCompletedData.best_score != null && (
                    <Grid item xs={4} sx={{ textAlign: 'center' }}>
                      <Typography variant="h5" fontWeight={700} color="primary.main">
                        {alreadyCompletedData.best_score}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Best Score</Typography>
                    </Grid>
                  )}
                  {alreadyCompletedData.total_questions != null && (
                    <Grid item xs={4} sx={{ textAlign: 'center' }}>
                      <Typography variant="h5" fontWeight={700} color="info.main">
                        {alreadyCompletedData.total_questions}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Questions</Typography>
                    </Grid>
                  )}
                  <Grid item xs={4} sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={700} color="success.main">
                      {alreadyCompletedData.total_attempts}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Attempts</Typography>
                  </Grid>
                </Grid>
              </Paper>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button variant="outlined" onClick={() => navigate(-1)} startIcon={<ArrowBack />}>
                  Go Back
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setAlreadyCompletedData(null)}
                  sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}
                >
                  Review Test
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Container>
    );
  }

  if (!testData) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Avatar sx={{ width: 60, height: 60, mx: 'auto', mb: 2, bgcolor: 'error.main' }}>
            <Cancel sx={{ fontSize: 30 }} />
          </Avatar>
          <Typography variant="h6" color="error" gutterBottom>
            Test Not Available
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The requested test could not be found or is no longer available.
          </Typography>
          <Button onClick={() => navigate(-1)} variant="contained">
            Go Back
          </Button>
        </Card>
      </Container>
    );
  }

  // Test Instructions Screen
  if (!testStarted) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Security Alert */}
        <Fade in timeout={400}>
          <Alert
            severity="info"
            icon={<Security />}
            sx={{ mb: 3, borderRadius: 2 }}
          >
            <Typography variant="body2">
              <strong>Secure Test Environment:</strong> This test is monitored for security.
              Please do not switch tabs or leave this page during the test.
            </Typography>
          </Alert>
        </Fade>

        <Fade in timeout={600}>
          <Card sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: '20px',
            boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)',
            mb: 4
          }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
                <Tooltip title="Go Back" arrow>
                  <IconButton
                    onClick={() => navigate(-1)}
                    sx={{
                      background: 'rgba(255,255,255,0.15)',
                      color: 'white',
                      '&:hover': { background: 'rgba(255,255,255,0.25)' }
                    }}
                  >
                    <ArrowBack />
                  </IconButton>
                </Tooltip>

                <Avatar sx={{
                  background: 'rgba(255,255,255,0.2)',
                  width: 60,
                  height: 60
                }}>
                  <Quiz sx={{ fontSize: 30 }} />
                </Avatar>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                    📝 {testData.test.name}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Topic Assignment Instructions & Overview
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Fade>

        <Grid container spacing={4}>
          {/* Test Overview */}
          <Grid item xs={12} md={8}>
            <Fade in timeout={800}>
              <Card sx={{ height: 'fit-content' }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Avatar sx={{ bgcolor: 'info.main' }}>
                      <Info />
                    </Avatar>
                    <Typography variant="h5" fontWeight={600}>
                      Test Instructions
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 4 }}>
                    <Typography variant="body1" paragraph>
                      Welcome to the <strong>{testData.test.name}</strong>. Please read the following instructions carefully before starting:
                    </Typography>

                    <Box component="ul" sx={{ pl: 3, mb: 3 }}>
                      <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                        This test contains <strong>{testData.test.questions.length} questions</strong>
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                        Total time allocated: <strong>{formatTime(timeRemaining)}</strong>
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                        Each question has a specific time limit
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                        You can navigate between questions using Next/Previous buttons
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                        Your progress will be saved automatically
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                        Once time runs out, the test will be submitted automatically
                      </Typography>
                    </Box>

                    <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 }, bgcolor: 'error.50', border: '1px solid', borderColor: 'error.200', mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'error.main', width: 32, height: 32 }}>
                          <Security sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Typography variant="h6" color="error.dark">
                          Security Guidelines
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="error.dark">
                        • Do not switch tabs or minimize the browser during the test<br />
                        • Do not use external resources or assistance<br />
                        • Ensure you have a stable internet connection<br />
                        • Complete the test in one sitting without interruption<br />
                        • Any suspicious activity may result in test termination
                      </Typography>
                    </Paper>

                    <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 }, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'warning.main', width: 32, height: 32 }}>
                          <Flag sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Typography variant="h6" color="warning.dark">
                          Important Notes
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="warning.dark">
                        • Make sure you have a stable internet connection<br />
                        • Do not refresh the page during the test<br />
                        • Answer all questions to maximize your score<br />
                        • Review your answers before final submission
                      </Typography>
                    </Paper>
                  </Box>

                  <Box sx={{ textAlign: 'center' }}>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<PlayArrow />}
                      onClick={handleStartTest}
                      sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '25px',
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                        '&:hover': {
                          boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      Start Test
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Fade>
          </Grid>

          {/* Test Statistics */}
          <Grid item xs={12} md={4}>
            <Fade in timeout={1000}>
              <Card>
                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Avatar sx={{ bgcolor: 'success.main' }}>
                      <Assignment />
                    </Avatar>
                    <Typography variant="h6" fontWeight={600}>
                      Test Overview
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Total Questions
                      </Typography>
                      <Chip
                        label={testData.test.questions.length}
                        sx={{ bgcolor: 'primary.main', color: 'white' }}
                        size="small"
                      />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Total Duration
                      </Typography>
                      <Chip
                        label={formatTime(timeRemaining)}
                        sx={{ bgcolor: 'secondary.main', color: 'white' }}
                        size="small"
                      />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Question Types
                      </Typography>
                      <Chip
                        label="Multiple Choice"
                        sx={{ bgcolor: 'info.main', color: 'white' }}
                        size="small"
                      />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Security Level
                      </Typography>
                      <Chip
                        label="High"
                        sx={{ bgcolor: 'error.main', color: 'white' }}
                        size="small"
                        icon={<Security />}
                      />
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                    Good luck with your test! 🍀
                  </Typography>
                </CardContent>
              </Card>
            </Fade>
          </Grid>
        </Grid>
      </Container>
    );
  }

  // Test Interface
  const currentQuestion = testData.test.questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / testData.test.questions.length) * 100;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Gradient header */}
      <Card sx={{
        background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
        color: 'white',
        borderRadius: '15px',
        mb: 3,
      }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                <Quiz />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                  {testData.test.name}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Question {currentQuestionIndex + 1} of {testData.test.questions.length}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <AccessTime sx={{ fontSize: 20 }} />
                <Typography variant="h6" fontWeight={600}>
                  {isPaused ? `${formatTime(timeRemaining)} — Paused` : formatTime(timeRemaining)}
                </Typography>
              </Box>

              <Chip
                label={`${getAnsweredCount()}/${testData.test.questions.length} Answered`}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
              />

              {isPaused ? (
                <Chip
                  icon={<PlayCircleOutline sx={{ color: 'white !important' }} />}
                  label="Resume"
                  onClick={handleResume}
                  sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 700, cursor: 'pointer' }}
                />
              ) : (
                <Chip
                  icon={<Pause sx={{ color: 'white !important' }} />}
                  label="Pause"
                  onClick={handlePause}
                  sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 600, cursor: 'pointer' }}
                />
              )}

              <Chip
                icon={<Security sx={{ color: 'white !important' }} />}
                label="Secure"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                size="small"
              />
            </Box>
          </Box>

          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: 'rgba(255,255,255,0.2)',
              '& .MuiLinearProgress-bar': { bgcolor: 'white' },
            }}
          />
        </CardContent>
      </Card>

      {/* Question navigator */}
      {testData.test.questions.length > 1 && (
        <Box sx={{ p: 2, mb: 2, bgcolor: '#f8f9fb', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary', mb: 1.5 }}>
            Jump to Question
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
            {[
              { label: 'Not Visited', key: STATUS.NOT_VISITED },
              { label: 'Unanswered', key: STATUS.UNANSWERED },
              { label: 'Answered', key: STATUS.ANSWERED },
              { label: 'Flagged', key: STATUS.FLAGGED },
            ].map(({ label, key }) => (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: STATUS_COLORS[key].bg, border: `1.5px solid ${STATUS_COLORS[key].border}` }} />
                <Typography variant="caption" color="text.secondary">{label}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, maxHeight: 84, overflowY: 'auto', pr: 0.5,
            '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 2 } }}>
            {testData.test.questions.map((q, index) => {
              const isCurrent = index === currentQuestionIndex;
              const status = questionStatus[q.id] ?? STATUS.NOT_VISITED;
              const colors = STATUS_COLORS[status];
              return (
                <Button
                  key={index}
                  size="small"
                  variant={isCurrent ? 'contained' : 'outlined'}
                  onClick={() => goToQuestion(index)}
                  sx={{
                    minWidth: 36, width: 36, height: 36, p: 0,
                    fontSize: '0.82rem', fontWeight: isCurrent ? 700 : 500, borderRadius: 1.5,
                    borderColor: isCurrent ? 'primary.main' : colors.border,
                    color: isCurrent ? 'white' : colors.text,
                    bgcolor: isCurrent ? 'primary.main' : colors.bg,
                    '&:hover': { opacity: 0.82 },
                    transition: 'all 0.15s',
                  }}
                >
                  {index + 1}
                </Button>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Paused banner */}
      {isPaused && (
        <Card sx={{ mb: 2, borderRadius: 2, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200', boxShadow: '0 2px 8px rgba(237,108,2,0.12)' }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Pause color="warning" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 700, color: 'warning.dark' }}>Test Paused</Typography>
                <Typography variant="body2" color="text.secondary">Your progress has been saved. Press Resume to continue.</Typography>
              </Box>
              <Button variant="contained" color="success" startIcon={<PlayCircleOutline />} onClick={handleResume} sx={{ fontWeight: 700, borderRadius: 1.5 }}>
                Resume
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Question Card */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', opacity: isPaused ? 0.4 : 1, pointerEvents: isPaused ? 'none' : 'auto' }}>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          {/* Question header row */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip label={`Q ${currentQuestionIndex + 1}`} size="small" color="primary" sx={{ fontWeight: 700 }} />
              {flaggedQuestions.has(currentQuestion.id) && (
                <Chip label="Flagged" size="small" color="warning" icon={<Flag sx={{ fontSize: 14 }} />} sx={{ fontWeight: 600 }} />
              )}
            </Box>
            <Button
              size="small"
              variant={flaggedQuestions.has(currentQuestion.id) ? 'contained' : 'outlined'}
              color="warning"
              startIcon={flaggedQuestions.has(currentQuestion.id) ? <Flag /> : <FlagOutlined />}
              onClick={() => handleFlagQuestion(currentQuestion.id)}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              {flaggedQuestions.has(currentQuestion.id) ? 'Unflag' : 'Flag'}
            </Button>
          </Box>

          {/* Question type badge */}
          <Chip label="Multiple Choice" size="small" variant="filled"
            sx={{ mb: 1.5, fontSize: '0.72rem', fontWeight: 700, bgcolor: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c933' }} />

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ lineHeight: 1.6 }}>
              {currentQuestion.name}
            </Typography>
            <Chip
              icon={<Timer sx={{ fontSize: 15, color: 'info.main !important' }} />}
              label={`${currentQuestion.duration_in_sec}s per question`}
              variant="outlined"
              size="small"
              sx={{ borderColor: 'info.main', color: 'info.main', mt: 1 }}
            />
          </Box>

          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={answers[currentQuestion.id] || ''}
              onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
            >
              {currentQuestion.options.map((option) => {
                const isSelected = answers[currentQuestion.id] === option.id.toString();
                return (
                  <Paper
                    key={option.id}
                    sx={{
                      p: 2, mb: 2,
                      border: isSelected ? '2px solid #667eea' : '1px solid #e0e0e0',
                      borderRadius: 2,
                      cursor: 'pointer',
                      bgcolor: isSelected ? 'primary.50' : 'transparent',
                      transition: 'all 0.3s ease',
                      '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.1)', transform: 'translateY(-1px)' },
                    }}
                    onClick={() => handleAnswerChange(currentQuestion.id, option.id.toString())}
                  >
                    <FormControlLabel
                      value={option.id.toString()}
                      control={<Radio />}
                      label={<Typography variant="body1" sx={{ ml: 1 }}>{option.name}</Typography>}
                      sx={{ width: '100%', m: 0 }}
                    />
                  </Paper>
                );
              })}
            </RadioGroup>
          </FormControl>
        </CardContent>
      </Card>

      {/* Navigation Card */}
      <Card>
        <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              startIcon={<NavigateBefore />}
              onClick={handlePreviousQuestion}
              disabled={currentQuestionIndex === 0}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            >
              Previous
            </Button>

            <Box sx={{ display: 'flex', gap: 2 }}>
              {currentQuestionIndex === testData.test.questions.length - 1 ? (
                <Button
                  variant="contained"
                  color={testCompleted ? 'inherit' : 'success'}
                  startIcon={submitting ? null : testCompleted ? <ArrowBack /> : <CheckCircle />}
                  onClick={() => testCompleted ? navigate(-1) : setShowConfirmDialog(true)}
                  disabled={submitting}
                  sx={{ px: 4, borderRadius: 2 }}
                >
                  {submitting ? 'Submitting...' : testCompleted ? 'Back to Content' : 'Submit Test'}
                </Button>
              ) : (
                <Button
                  endIcon={<NavigateNext />}
                  onClick={handleNextQuestion}
                  variant="contained"
                  sx={{ px: 4, borderRadius: 2 }}
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Submit Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'warning.main' }}>
              <Flag />
            </Avatar>
            Submit Test?
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to submit your test?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You have answered {getAnsweredCount()} out of {testData.test.questions.length} questions.
            Once submitted, you cannot make any changes.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitTest}
            variant="contained"
            sx={{
              bgcolor: 'success.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'success.dark'
              }
            }}
            startIcon={<CheckCircle />}
          >
            Submit Test
          </Button>
        </DialogActions>
      </Dialog>

      {/* Processing Dialog */}
      <Dialog
        open={showProcessing}
        onClose={() => { }} // Prevent closing during processing
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ textAlign: 'center', py: 6 }}>
          {processingStep === 'processing' && (
            <Fade in timeout={600}>
              <Box>
                <Avatar sx={{
                  width: 80,
                  height: 80,
                  mx: 'auto',
                  mb: 3,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  animation: 'pulse 2s infinite'
                }}>
                  <Quiz sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  Processing Your Test...
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Please wait while we evaluate your answers
                </Typography>
                <LinearProgress
                  sx={{
                    width: '100%',
                    height: 8,
                    borderRadius: 4,
                    background: 'rgba(102, 126, 234, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    }
                  }}
                />
              </Box>
            </Fade>
          )}

          {processingStep === 'success' && (
            <Fade in timeout={600}>
              <Box>
                <Avatar sx={{
                  width: 80,
                  height: 80,
                  mx: 'auto',
                  mb: 3,
                  bgcolor: 'success.main',
                  animation: 'bounce 0.6s ease-in-out'
                }}>
                  <CheckCircle sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h5" fontWeight={600} gutterBottom color="success.main">
                  Test Submitted Successfully!
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Preparing your results...
                </Typography>
              </Box>
            </Fade>
          )}
        </DialogContent>
      </Dialog>

      {/* Test Results Dialog */}
      <Dialog
        open={showResultsDialog}
        onClose={() => {
          setShowResultsDialog(false);
          navigate(-1);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{
              bgcolor: testResults?.score === 100 ? 'success.main' : testResults?.score >= 70 ? 'info.main' : 'warning.main'
            }}>
              {testResults?.score === 100 ? <CheckCircle /> : <Quiz />}
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                Test Results
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {testData?.test?.name}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {testResults && (
            <Grid container spacing={3}>
              {/* Score Card */}
              <Grid item xs={12} sm={6}>
                <Card sx={{
                  background: testResults.score === 100
                    ? 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)'
                    : testResults.score >= 70
                      ? 'linear-gradient(135deg, #2196f3 0%, #03a9f4 100%)'
                      : 'linear-gradient(135deg, #ff9800 0%, #ffc107 100%)',
                  color: 'white',
                  textAlign: 'center'
                }}>
                  <CardContent>
                    <Typography variant="h3" fontWeight={700}>
                      {testResults.score}%
                    </Typography>
                    <Typography variant="body1">
                      Your Score
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Correct Answers Card */}
              <Grid item xs={12} sm={6}>
                <Card sx={{ textAlign: 'center' }}>
                  <CardContent>
                    <Typography variant="h4" fontWeight={600} color="success.main">
                      {testResults.correct_answers}/{testResults.total_questions}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Correct Answers
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Additional Stats */}
              <Grid item xs={12}>
                <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" fontWeight={600}>
                          {testResults.attempt_count}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Attempt
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" fontWeight={600}>
                          {Math.floor(testResults.total_time_taken / 60)}m {testResults.total_time_taken % 60}s
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Time Taken
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" fontWeight={600}>
                          {testResults.total_questions - testResults.correct_answers}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Incorrect
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Chip
                          label={testResults.is_completed ? 'Completed' : 'In Progress'}
                          color={testResults.is_completed ? 'success' : 'warning'}
                          variant="filled"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Status
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Performance Message */}
              <Grid item xs={12}>
                <Alert
                  severity={
                    testResults.score === 100 ? 'success' :
                      testResults.score >= 70 ? 'info' : 'warning'
                  }
                  sx={{ borderRadius: 2 }}
                >
                  <Typography variant="body1" fontWeight={600}>
                    {testResults.score === 100
                      ? '🎉 Perfect Score! Excellent work!'
                      : testResults.score >= 70
                        ? '👍 Good job! You passed the test.'
                        : '📚 Keep practicing to improve your score.'}
                  </Typography>
                  <Typography variant="body2">
                    {testResults.score === 100
                      ? 'You answered all questions correctly. Outstanding performance!'
                      : testResults.score >= 70
                        ? 'You have a good understanding of the topic. Well done!'
                        : 'Review the topics and try again to improve your understanding.'}
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowResultsDialog(false);
              navigate(-1);
            }}
            variant="contained"
            sx={{ px: 4 }}
          >
            Back to Content
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TopicTest;
